import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingAdCTA from "./components/FloatingAdCTA";

import Home from "./pages/Home";
import Magazine from "./pages/Magazine";
import MagazineReader from "./pages/MagazineReader";
import Flipbook from "./pages/Flipbook";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Founder from "./pages/Founder";
import Edito from "./pages/Edito";
import SpecialEdition from "./pages/SpecialEdition";
import Services from "./pages/Services";
import Quote from "./pages/Quote";
import Appointment from "./pages/Appointment";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Clients from "./pages/admin/Clients";
import Questionnaires from "./pages/admin/Questionnaires";
import Proposals from "./pages/admin/Proposals";
import Offers from "./pages/admin/Offers";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminMagazines from "./pages/admin/AdminMagazines";
import AdminSpecialEditions from "./pages/admin/AdminSpecialEditions";
import AdminPlaceholder from "./pages/admin/AdminPlaceholder";
import AuthCallback from "./pages/AuthCallback";
import QuestionnaireFill from "./pages/QuestionnaireFill";
import ProposalView from "./pages/ProposalView";
import Advertise from "./pages/Advertise";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import QuestionnairePublic from "./pages/QuestionnairePublic";
import ProposalPublic from "./pages/ProposalPublic";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-center">Chargement…</div>;
  if (!user || user === false) return <Navigate to="/connexion" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

// Chromeless pages (no header/footer): flipbook reader, article detail (ivory mode), auth callback
const CHROMELESS_PREFIXES = ["/magazine/", "/articles/", "/auth/callback", "/admin", "/questionnaire/", "/proposition/"];

function Shell({ children }) {
  const { pathname } = useLocation();
  const chromeless = CHROMELESS_PREFIXES.some((p) => pathname.startsWith(p));
  // Hide floating CTA on auth/admin/checkout/public-token pages (chromeless) — show on editorial public pages
  const hideFloat = chromeless || pathname === "/connexion" || pathname === "/inscription" || pathname.startsWith("/annoncer") || pathname.startsWith("/achat/");
  return (
    <>
      {!chromeless && <Header />}
      <main>{children}</main>
      {!chromeless && <Footer />}
      {!hideFloat && <FloatingAdCTA />}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent Google Auth callback via URL fragment (must be before routes)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/magazine" element={<Magazine />} />
        <Route path="/magazine/:id" element={<MagazineReader />} />
        <Route path="/magazine/:id/pdf" element={<Flipbook />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<ArticleDetail />} />
        <Route path="/fondatrice" element={<Founder />} />
        <Route path="/edito" element={<Edito />} />
        <Route path="/edition-speciale/:slug" element={<SpecialEdition />} />
        <Route path="/regie" element={<Services />} />
        <Route path="/devis" element={<Advertise />} />
        <Route path="/annoncer" element={<Advertise />} />
        <Route path="/devis/questionnaire/:token" element={<QuestionnaireFill />} />
        <Route path="/devis/proposition/:token" element={<ProposalView />} />
        <Route path="/achat/succes" element={<CheckoutSuccess />} />
        <Route path="/questionnaire/:token" element={<QuestionnairePublic />} />
        <Route path="/proposition/:token" element={<ProposalPublic />} />
        <Route path="/rendez-vous" element={<Appointment />} />
        <Route path="/connexion" element={<Login />} />
        <Route path="/inscription" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="questionnaires" element={<Questionnaires />} />
          <Route path="leads" element={<AdminPlaceholder title="Leads" description="Pipeline de prospects qualifiés (annonceurs, partenaires, prospects rédaction)." />} />
          <Route path="articles" element={<AdminArticles />} />
          <Route path="magazines" element={<AdminMagazines />} />
          <Route path="editions-speciales" element={<AdminSpecialEditions />} />
          <Route path="devis" element={<Proposals />} />
          <Route path="offres" element={<Offers />} />
          <Route path="annonceurs" element={<AdminPlaceholder title="Annonceurs" description="Catalogue d'offres, espaces publicitaires, campagnes annonceurs." />} />
          <Route path="rdv" element={<AdminPlaceholder title="Rendez-vous" description="Carnet de rendez-vous, confirmation, agenda direction." />} />
          <Route path="messagerie" element={<AdminPlaceholder title="Messagerie interne" description="Discussions internes, threads clients, notifications." />} />
          <Route path="sondages" element={<AdminPlaceholder title="Sondages & enquêtes" description="Questionnaires lecteurs, enquêtes éditoriales, baromètre annonceurs." />} />
          <Route path="equipe" element={<AdminPlaceholder title="Équipe interne" description="Rôles & permissions (rédaction, commercial, comptabilité, direction)." />} />
        </Route>
        <Route
          path="/admin/console"
          element={
            <ProtectedRoute adminOnly>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
